import { INPUT_DEFAULT_VALUE, InputType } from "components/generators/inputsGenerator";
import qs, { ParsedQs } from "qs";
import _ from "lodash";
import { SetURLSearchParams } from "react-router-dom";
import { UseFormReset } from "react-hook-form";
import { cleanSpaceInString } from "helpers/validation/createValidation";
import { FilterField } from "layout/TableConstructor/types";
import dayjs from "dayjs";

// на применение фильтров
export const generateQueryOnSubmit = (
  data: Record<string, any>,
  filters: FilterField<object>[],
  setSearchParams: SetURLSearchParams,
  initParams?: qs.ParsedQs
) => {
  const params = { ...initParams };
  filters.forEach((filter) => {
    const { type, name } = filter;
    const filterValue = _.get(data, name);
    const setParam = (newData: string | object) => _.set(params, name, newData);
    if (!filterValue) return;
    switch (type) {
      case InputType.TEXT: {
        if (filter.isRange && filter.inputType === "number") {
          const value = filterValue as { to: string; from: string };
          if (value.to || value.from) {
            setParam(value);
          }
        } else {
          setParam(typeof filterValue === "string" ? cleanSpaceInString(filterValue) : filterValue);
        }
        break;
      }
      case InputType.DATE: {
        const dateValue = dayjs(filterValue);
        if (dateValue.isValid()) setParam(dateValue.format("YYYY-MM-DD"));
        else setParam(filterValue);
        break;
      }
      default: {
        setParam(typeof filterValue === "string" ? cleanSpaceInString(filterValue) : filterValue);
        break;
      }
    }
  });
  setSearchParams(qs.stringify(params));
};

// чистит инпуты и query параметры
export const onClearFilter = (
  filters: FilterField<object>[],
  reset: UseFormReset<any>,
  setSearchParams: SetURLSearchParams,
  initParams?: qs.ParsedQs
) => {
  const initParamsCopy = JSON.parse(JSON.stringify(initParams));

  filters.forEach((filter) => {
    delete initParamsCopy?.[filter.name];
  });

  setSearchParams(initParamsCopy);
  const params = {};
  filters.forEach((filter) => {
    const { name } = filter;
    const multiple = "multiple" in filter ? filter.multiple : undefined;
    const inputType = filter.type;
    let defaultValue = filter.defaultValue || INPUT_DEFAULT_VALUE[inputType];
    if (multiple) {
      defaultValue = [];
    } else if (filter.type === InputType.TEXT && filter.inputType === "number" && filter.isRange) {
      defaultValue = { to: "", from: "" };
    }
    _.set(params, name, defaultValue);
  });
  reset(params);
};

// заполняет форму фильтров из query параметров
export const onUseEffect = (filters: FilterField<object>[], reset: UseFormReset<any>, query: ParsedQs) => {
  const params = {};
  filters.forEach((filter) => {
    const inputType = filter.type;

    const isAutocomplete = inputType === InputType.AUTOCOMPLETE;
    const existingValue = _.get(query, filter.name);

    const setParam = (newData: any) => _.set(params, filter.name, newData);
    let inputValue;
    if (!isAutocomplete || (isAutocomplete && !filter.multiple)) {
      inputValue = existingValue || INPUT_DEFAULT_VALUE[inputType];
    } else inputValue = existingValue || [];
    setParam(inputValue);
  });
  if (Object.keys(params).length) {
    reset(params);
  }
};

// перед отправкой на бэк
export const generateFilterQuery = (filters: FilterField<object>[], query: ParsedQs): object | undefined => {
  let params = {};
  filters.forEach((filter) => {
    const queryValue = _.get(query, filter.name);

    if (queryValue) {
      const filterName = filter.name;
      const { customFilter } = filter;
      let preparedValue = queryValue;

      if (filter.type === InputType.TEXT && filter.inputType === "number" && queryValue) {
        if (filter.isRange) {
          const value = queryValue as { to: string; from: string };
          // @ts-expect-error пофиксить типы
          preparedValue = customFilter
            ? { to: customFilter(value.to), from: customFilter(value.from) }
            : { from: +value.from || 0, to: +value.to || 0 };
        } else if (customFilter) {
          // @ts-expect-error пофиксить типы
          preparedValue = customFilter(queryValue);
        } else {
          // @ts-expect-error пофиксить типы
          preparedValue = queryValue ? +queryValue : undefined;
        }
      } else if (filter.type === InputType.DATE) {
        if (queryValue) {
          // @ts-expect-error пофиксить типы
          preparedValue = customFilter ? customFilter(queryValue) : dayjs.utc(queryValue).startOf("day").toISOString();
        }
      } else if (customFilter) {
        // @ts-expect-error пофиксить типы
        preparedValue = customFilter(queryValue);
      }

      // блок с проверкой на filterArrayItemParams сделан для специфичных фильтров запроса
      // VacancyCompanyCandidatesQuery (фильтр criterionMatch), но постарался сделать унифицированным,
      // без привязки конкретно к данному фильтру. Поэтому в случае похожей структуры фильтров можно переиспользовать
      const filterArrayItemParams = filter.arrayItemParams;
      let resultValue = preparedValue;
      let resultFilterName = filterName;

      if (filterArrayItemParams) {
        const parentFilterParam = (_.get(params, filterArrayItemParams.parentFilterName) || []) as Record<
          string,
          string | { to: string; form: string }
        >[];

        const filterParamIndex = parentFilterParam.findIndex(
          (item) => item[filterArrayItemParams.name.key] === filterArrayItemParams.name.value
        );

        const filterParamPreparedValue = {
          [filterArrayItemParams.name.key]: filterArrayItemParams.name.value,
          [filterArrayItemParams.value.key]: preparedValue,
        };

        if (filterParamIndex === -1) {
          resultValue = [...parentFilterParam, filterParamPreparedValue];
        } else {
          resultValue = [
            ...parentFilterParam.slice(0, filterParamIndex),
            ...parentFilterParam.slice(filterParamIndex + 1),
            filterParamPreparedValue,
          ];
        }

        resultFilterName = filterArrayItemParams.parentFilterName;
      }

      _.set(params, resultFilterName, resultValue);

      if (filter.additionalFilter) {
        params = { ...params, ...filter.additionalFilter };
      }
    }
  });

  return Object.keys(params).length === 0 ? undefined : params;
};
